import type {
  BossAgentAnswer,
  OpsTask,
  SessionUser
} from "@boss-agent/contracts";

export interface DashboardData {
  summary: {
    stores: number;
    orders: number;
    revenue: number;
    adSpend: number;
    visitors: number;
    conversion: number;
    pendingTasks: number;
    runningTasks: number;
    devicesOnline: number;
  };
  platformAdSpend: Record<string, number>;
  stores: Array<{
    id: string;
    name: string;
    city: string;
    owner: string;
    status: string;
    revenue: number;
    orders: number;
    visitors: number;
    conversion: number;
    platformIds: Record<string, string>;
    accountNames: Record<string, string>;
  }>;
  trend: Array<{ date: string; revenue: number; orders: number }>;
  alerts: Array<{
    id: string;
    storeId?: string;
    storeName: string;
    level: string;
    title: string;
    description: string;
  }>;
  recentTasks: OpsTask[];
}

export interface MetricRecord {
  id: string;
  organizationId: string;
  storeId: string;
  metricDate: string;
  channel: string;
  revenue: number;
  adSpend: number;
  orders: number;
  visitors: number;
  conversion: number;
}

export interface DeviceInfo {
  id: string;
  name: string;
  platform: string;
  status: "online" | "offline" | "revoked";
  lastSeenAt?: string;
  pairedAt: string;
}

export interface PairingCode {
  code: string;
  expiresAt: string;
}

export interface ContentAsset {
  id: string;
  storeId?: string;
  storeName?: string;
  type: "image" | "video" | "document";
  fileId: string;
  url: string;
  taskId?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface TakeoutIntegration {
  id: "meituan" | "taobao_flash" | "jingdong";
  name: string;
  authorizationMode: string;
  applicationUrl: string;
  documentationUrl: string;
  sdkUrl: string;
  callbackUrl: string;
  capabilities: string[];
  credentialConfigured: boolean;
  status: "not_configured" | "configured";
}

export type TakeoutPlatformId = "meituan" | "taobao_flash" | "jingdong";

export interface TakeoutAccount {
  id: string;
  organizationId: string;
  storeId: string;
  storeName: string;
  platform: TakeoutPlatformId;
  platformName: string;
  accountName: string;
  externalStoreId: string;
  status: "active" | "inactive";
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface TakeoutPlan {
  id: string;
  organizationId: string;
  storeId: string;
  storeName: string;
  platform: TakeoutPlatformId;
  platformName: string;
  planType: "promotion" | "decoration" | "menu" | "review";
  title: string;
  objective: string;
  status: "draft" | "approved" | "executing" | "completed" | "cancelled";
  budget: number;
  targetRoi: number;
  startsOn?: string;
  endsOn?: string;
  details: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface PromotionPolicy {
  id: string;
  organizationId: string;
  platform: TakeoutPlatformId;
  platformName: string;
  enabled: boolean;
  mode: "suggest" | "guarded_auto";
  dailyBudgetCap: number;
  bidCap: number;
  maxChangePercent: number;
  targetRoi: number;
  minSpendForEvaluation: number;
  stopLossRoi: number;
  observationDays: number;
  requireCompleteData: boolean;
  metricWindowDays: number;
  preferredTimeRange: string;
  notes: string;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface Envelope<T> {
  requestId: string;
  code: number;
  data: T;
  message: string;
}

let csrfToken = "";
const API_TOKEN_KEY = "boss-api-token";
let apiToken = sessionStorage.getItem(API_TOKEN_KEY) || "";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  if (csrfToken && init.method && init.method !== "GET") {
    headers.set("x-csrf-token", csrfToken);
  }
  if (apiToken) {
    headers.set("authorization", `Bearer ${apiToken}`);
    headers.set("x-boss-session", apiToken);
  }
  let body = init.body;
  if (
    apiToken &&
    init.method &&
    init.method !== "GET" &&
    typeof body === "string" &&
    headers.get("content-type")?.includes("application/json")
  ) {
    const payload = JSON.parse(body) as Record<string, unknown>;
    payload.sessionToken = payload.sessionToken || apiToken;
    payload.csrfToken = payload.csrfToken || csrfToken;
    body = JSON.stringify(payload);
  }
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    body,
    headers
  });
  const responseBody = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(
      response.status,
      responseBody.error || responseBody.message || `请求失败：${response.status}`
    );
  }
  return responseBody as T;
}

export async function login(username: string, password: string): Promise<SessionUser> {
  const envelope = await request<Envelope<{ user: SessionUser; csrfToken: string; apiToken: string }>>("/v1/web/login", {
    method: "POST",
    body: JSON.stringify({ username, password })
  });
  if (envelope.code !== 0) throw new ApiError(envelope.code, envelope.message || "登录失败");
  const result = envelope.data;
  csrfToken = result.csrfToken;
  apiToken = result.apiToken;
  sessionStorage.setItem(API_TOKEN_KEY, apiToken);
  return result.user;
}

export async function getSession(): Promise<SessionUser | null> {
  try {
    const envelope = await request<Envelope<{ user: SessionUser; csrfToken: string }>>("/v1/web/session", {
      method: "POST",
      body: "{}"
    });
    if (envelope.code !== 0) throw new ApiError(envelope.code, envelope.message || "会话无效");
    const result = envelope.data;
    csrfToken = result.csrfToken;
    return result.user;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      apiToken = "";
      sessionStorage.removeItem(API_TOKEN_KEY);
      return null;
    }
    throw error;
  }
}

export async function logout(): Promise<void> {
  await request("/v1/web/logout", { method: "POST", body: "{}" });
  csrfToken = "";
  apiToken = "";
  sessionStorage.removeItem(API_TOKEN_KEY);
}

export async function changePassword(password: string): Promise<void> {
  await request("/v1/web/password", {
    method: "POST",
    body: JSON.stringify({ password })
  });
}

export async function callApi<T>(action: string, payload: object = {}): Promise<T> {
  const envelope = await request<Envelope<T>>("/v1/api", {
    method: "POST",
    body: JSON.stringify({ action, payload })
  });
  if (envelope.code !== 0) {
    throw new ApiError(envelope.code, envelope.message || "服务返回错误");
  }
  return envelope.data;
}

export function getDashboard(): Promise<DashboardData> {
  return callApi<DashboardData>("boss.dashboard");
}

export function listMetricRecords(input: {
  storeId?: string;
  startDate?: string;
  endDate?: string;
  channel?: string;
} = {}): Promise<MetricRecord[]> {
  return callApi<MetricRecord[]>("metric.list", input);
}

export function listTasks(): Promise<OpsTask[]> {
  return callApi<OpsTask[]>("task.list");
}

export function getTask(id: string): Promise<OpsTask> {
  return callApi<OpsTask>("task.get", { id });
}

export function createTask(input: Partial<OpsTask> & Pick<OpsTask, "title" | "taskType">): Promise<OpsTask> {
  return callApi<OpsTask>("task.create", input);
}

export function transitionTask(id: string, status: OpsTask["status"]): Promise<OpsTask> {
  return callApi<OpsTask>("task.transition", { id, status });
}

export function askBossAgent(prompt: string, storeIds: string[], periodDays: number): Promise<BossAgentAnswer> {
  return callApi<BossAgentAnswer>("boss.ask", { prompt, storeIds, periodDays });
}

export interface PeriodReport {
  periodDays: number;
  startDate: string;
  endDate: string;
  summary: { orders: number; revenue: number; adSpend: number; roi: number };
  anomalies: Array<{ date: string; level: string; message: string }>;
  platformComparison: Array<{
    platform: string;
    orders: number;
    revenue: number;
    adSpend: number;
    avgPrice: number;
    roi: number;
    share: number;
  }>;
  recommendations: Array<{ title: string; detail: string }>;
  markdown: string;
}

export function getPeriodReport(periodDays: number): Promise<PeriodReport> {
  return callApi<PeriodReport>("boss.period.report", { periodDays });
}

export function listDevices(): Promise<DeviceInfo[]> {
  return callApi<DeviceInfo[]>("device.list");
}

export function listPairingCodes(): Promise<PairingCode[]> {
  return callApi<PairingCode[]>("pairing.list");
}

export function createPairingCode(): Promise<PairingCode> {
  return callApi<PairingCode>("pairing.create");
}

export function revokeDevice(id: string): Promise<{ ok: true }> {
  return callApi("device.revoke", { id });
}

export function currentApiToken(): string {
  return apiToken;
}

export function currentCsrfToken(): string {
  return csrfToken;
}

export function listContentAssets(): Promise<ContentAsset[]> {
  return callApi<ContentAsset[]>("content.list");
}

export function registerContentAsset(input: {
  fileId: string;
  type: "image" | "video" | "document";
  storeId?: string;
  taskId?: string;
  metadata?: Record<string, unknown>;
}): Promise<ContentAsset> {
  return callApi<ContentAsset>("content.register", input);
}

export function listTakeoutIntegrations(): Promise<TakeoutIntegration[]> {
  return callApi<TakeoutIntegration[]>("takeout.integration.capabilities");
}

export function listTakeoutAccounts(input: {
  storeId?: string;
  platform?: TakeoutPlatformId;
} = {}): Promise<TakeoutAccount[]> {
  return callApi<TakeoutAccount[]>("takeout.account.list", input);
}

export function saveTakeoutAccount(input: {
  id?: string;
  storeId: string;
  platform: TakeoutPlatformId;
  accountName: string;
  externalStoreId: string;
  status: "active" | "inactive";
  notes?: string;
}): Promise<TakeoutAccount> {
  return callApi<TakeoutAccount>("takeout.account.save", input);
}

export function listTakeoutPlans(input: {
  storeId?: string;
  platform?: TakeoutPlatformId;
  planType?: "promotion" | "decoration" | "menu" | "review";
  status?: TakeoutPlan["status"];
} = {}): Promise<TakeoutPlan[]> {
  return callApi<TakeoutPlan[]>("takeout.plan.list", input);
}

export function updateTakeoutPlanStatus(
  planId: string,
  status: TakeoutPlan["status"]
): Promise<TakeoutPlan> {
  return callApi<TakeoutPlan>("takeout.plan.status", { planId, status });
}

export function listPromotionPolicies(): Promise<PromotionPolicy[]> {
  return callApi<PromotionPolicy[]>("promotion.policy.list");
}

export function savePromotionPolicy(
  input: Omit<PromotionPolicy, "id" | "organizationId" | "platformName" | "isDefault" | "createdAt" | "updatedAt">
): Promise<PromotionPolicy> {
  return callApi<PromotionPolicy>("promotion.policy.save", input);
}

export function generatePromotionPlans(input: {
  platforms?: TakeoutPlatformId[];
  storeIds?: string[];
} = {}): Promise<{
  created: number;
  updated: number;
  stores: number;
  platforms: TakeoutPlatformId[];
}> {
  return callApi("promotion.plan.generate", input);
}
